import CloseIcon from '@mui/icons-material/Close'
import LockIcon from '@mui/icons-material/Lock'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import HttpsIcon from '@mui/icons-material/Https'
import LoginIcon from '@mui/icons-material/Login'
import ScheduleIcon from '@mui/icons-material/Schedule'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import { IdlePopupaStyledDialog } from 'src/clinic/components/CheckIn/components/styledComponents'
import { DISPATCH_EVENT_KEYS, MOMENT_FORMATS } from 'src/consumer/constants'
import { getExactDiffFromCurrentTimeDayJsWithExtraData } from 'src/consumer/helpers/utilFunctions'
import { MUIThemeConfig } from 'src/context/MUIThemeConfig'
import { useIdleScreen } from 'src/customHooks/customHooks'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { MentorClinicsContext } from 'src/mentor/layout'
import styled from 'styled-components'
import {
  checkIntoRoomWithLocalStorageApplied,
  checkOutRoom,
  logMentorIdle,
  presentInRoom,
  refreshCheckIntoRoomWithLocalStorageApplied,
} from './services'

/**
 * Phase 14bi — discipline-agnostic check-in option icons. The previous text-match
 * approach ("Therapy only" → no icon hit; "Screening only" → no icon hit; only
 * "Consultation" matched) was both fragile and wrong: now that the therapy title is
 * dynamically rewritten to "Psychotherapy only" / "Nutrition Consultation only" /
 * "Therapy/Assessments" by the backend's discipline override, the keyword matching
 * misses every variant. The fix matches by the option's stable backend ID first
 * (`consultation-only`, `therapy-only`, `screening-only`, `therapy-and-or-screening`)
 * and only falls back to text matching for non-clinical contexts (the same component
 * is also used for check-in METHODS — In Person / Remotely — which are distinct from
 * event options and rely on text matching).
 *
 * Icon picks are intentionally generic so they read sensibly across every discipline
 * a future ops team might add (psychotherapy / nutrition / speech-therapy / ...):
 *   - consultation-only        → 👨‍⚕️ (a doctor; works for any consult type)
 *   - therapy-only             → 💬 (talk session; works for any therapy discipline)
 *   - screening-only           → 📋 (clipboard / intake; works for any screening track)
 *   - therapy-and-or-screening → 🧩 (combined / multi-purpose)
 */
const CHECKIN_OPTION_ICON_BY_ID = {
  'consultation-only': '👨‍⚕️',
  'therapy-only': '💬',
  'screening-only': '📋',
  'therapy-and-or-screening': '🧩',
}

export const CheckInDialogButtonComponent = ({
  id,
  imageLink,
  text,
  clickFunc,
  selected,
  disabled,
  fullWidth = false,
  description,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  // Phase 14bi — id-first icon resolution; text-match fallback covers check-in
  // method labels (In Person / Remotely / Phone) whose ids aren't in the event-
  // option map.
  const getDefaultIcon = () => {
    if (id && CHECKIN_OPTION_ICON_BY_ID[id]) {
      return CHECKIN_OPTION_ICON_BY_ID[id]
    }
    const lowerText = (text || '').toLowerCase()
    if (lowerText.includes('person') || lowerText.includes('clinic') || lowerText.includes('office')) {
      return '🏥'
    }
    if (lowerText.includes('remote') || lowerText.includes('video') || lowerText.includes('online')) {
      return '💻'
    }
    if (lowerText.includes('phone') || lowerText.includes('call')) {
      return '📞'
    }
    if (lowerText.includes('screen')) {
      return '📋'
    }
    if (lowerText.includes('therapy') || lowerText.includes('psychotherapy') ||
        lowerText.includes('nutrition') || lowerText.includes('assessment')) {
      return '💬'
    }
    if (lowerText.includes('consultation') || lowerText.includes('consult')) {
      return '👨‍⚕️'
    }
    return null
  }

  const defaultIcon = getDefaultIcon()
  
  return (
    <Box
      component="button"
      onClick={clickFunc}
      disabled={disabled}
      sx={{
        // Reset button styles
        border: 'none',
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',

        // Layout
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        // Phase 14bi — tighter inter-element gap; the previous 2-3 spacing pushed
        // the icon and label apart and ate vertical room on mobile.
        gap: { xs: 1.5, sm: 2 },

        // Sizing - Full width on mobile, flexible on larger screens
        width: fullWidth ? '100%' : { xs: '100%', sm: 'auto' },
        minWidth: { xs: '100%', sm: '200px', md: '240px' },
        maxWidth: '100%',
        flex: fullWidth ? '1 1 100%' : { xs: '1 1 100%', sm: '1 1 auto' },

        // Phase 14bi — compact padding so three options fit on one mobile screen
        // without scrolling on common 6"-class devices, and the dialog feels less
        // chunky on desktop. Was 16/18/20 → now 12/14/16.
        padding: { xs: '12px 14px', sm: '14px 18px', md: '16px 20px' },

        // Visual styling
        borderRadius: { xs: '12px', sm: '14px' },
        background: selected
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : disabled
          ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        boxShadow: selected
          ? '0 8px 24px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(118, 75, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
          : disabled
          ? '0 1px 3px rgba(0, 0, 0, 0.05)'
          : '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
        
        // Border
        outline: selected
          ? '2px solid rgba(102, 126, 234, 0.5)'
          : disabled
          ? '1px solid #e2e8f0'
          : '1px solid #e2e8f0',
        outlineOffset: selected ? '2px' : '0',
        
        // State
        opacity: disabled ? 0.6 : 1,
        transform: selected ? 'translateY(-2px)' : 'none',
        
        // Transitions
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        
        // Position for badge
        position: 'relative',
        overflow: 'visible',
        
        // Text color
        color: disabled ? '#94a3b8' : selected ? '#ffffff' : '#1e293b',
        
        // Hover states
        '&:hover:not(:disabled)': {
          background: selected
            ? 'linear-gradient(135deg, #5a6fd6 0%, #6a4299 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          boxShadow: selected
            ? '0 12px 32px rgba(102, 126, 234, 0.5), 0 6px 16px rgba(118, 75, 162, 0.35)'
            : '0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
          transform: selected ? 'translateY(-3px)' : 'translateY(-1px)',
          outline: selected
            ? '2px solid rgba(102, 126, 234, 0.6)'
            : '1px solid #cbd5e1',
        },
        
        // Active state
        '&:active:not(:disabled)': {
          transform: 'translateY(0)',
          boxShadow: selected
            ? '0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 6px rgba(118, 75, 162, 0.2)'
            : '0 1px 4px rgba(0, 0, 0, 0.08)',
        },
        
        // Focus visible
        '&:focus-visible': {
          outline: '3px solid rgba(102, 126, 234, 0.5)',
          outlineOffset: '3px',
        },
      }}
    >
      {/* Icon/Image Container — Phase 14bi: shrunk from 44/48/52 to 36/40/44 to
          keep three options on a single mobile viewport and to lower the chrome
          weight on desktop. defaultIcon is now id-first (see CHECKIN_OPTION_ICON_BY_ID
          above), so the box renders consistently across consultation / therapy /
          screening / combined options. */}
      {(imageLink || defaultIcon) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: { xs: '36px', sm: '40px', md: '44px' },
            height: { xs: '36px', sm: '40px', md: '44px' },
            borderRadius: { xs: '8px', sm: '10px' },
            background: selected
              ? 'rgba(255, 255, 255, 0.2)'
              : disabled
              ? 'rgba(148, 163, 184, 0.1)'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: selected
              ? '1px solid rgba(255, 255, 255, 0.3)'
              : '1px solid rgba(102, 126, 234, 0.15)',
            transition: 'all 0.25s ease',
          }}
        >
          {imageLink ? (
            <img
              src={imageLink}
              alt=""
              style={{
                width: isMobile ? '20px' : isTablet ? '22px' : '24px',
                height: isMobile ? '20px' : isTablet ? '22px' : '24px',
                objectFit: 'contain',
                opacity: disabled ? 0.5 : 1,
              }}
            />
          ) : defaultIcon ? (
            <span style={{
              fontSize: isMobile ? '18px' : isTablet ? '20px' : '22px',
              opacity: disabled ? 0.5 : 1,
            }}>
              {defaultIcon}
            </span>
          ) : null}
        </Box>
      )}
      
      {/* Text Content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          textAlign: 'left',
        }}
      >
        <Typography
          component="span"
          sx={{
            // Phase 14bi — slightly smaller than before so longer titles like
            // "Nutrition Consultation and/or Screening" fit on one line on most
            // mobile widths.
            fontSize: { xs: '14px', sm: '15px', md: '16px' },
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
            color: 'inherit',
            wordBreak: 'break-word',
            hyphens: 'auto',
            width: '100%',
          }}
        >
          {text}
        </Typography>
        {description && (
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '11px', sm: '12px' },
              fontWeight: 400,
              color: selected ? 'rgba(255, 255, 255, 0.85)' : '#64748b',
              lineHeight: 1.4,
              width: '100%',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      
      {/* Selection Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: { xs: '24px', sm: '26px', md: '28px' },
          height: { xs: '24px', sm: '26px', md: '28px' },
          borderRadius: '50%',
          background: selected
            ? 'rgba(255, 255, 255, 0.25)'
            : 'transparent',
          border: selected
            ? '2px solid rgba(255, 255, 255, 0.5)'
            : '2px solid #e2e8f0',
          transition: 'all 0.25s ease',
        }}
      >
        {selected && (
          <CheckCircleIcon 
            sx={{ 
              fontSize: { xs: '18px', sm: '20px', md: '22px' },
              color: 'white',
            }} 
          />
        )}
      </Box>
      
      {/* Disabled Badge */}
      {disabled && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '-8px', sm: '-10px' },
            right: { xs: '-4px', sm: '-6px' },
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#fff',
            padding: { xs: '3px 8px', sm: '4px 10px' },
            borderRadius: '6px',
            fontSize: { xs: '9px', sm: '10px' },
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
            zIndex: 1,
          }}
        >
          Unavailable
        </Box>
      )}
    </Box>
  )
}

export const CheckInDialogSubmitButtonComponent = ({ text, clickFunc, loading }) => {
  return (
    <CheckINDialogSubmitButton onClick={clickFunc} disabled={loading}>
      {text} {loading && <Spinner size="sm" color="light" />}
    </CheckINDialogSubmitButton>
  )
}

const CheckINDialogSubmitButton = styled.button`
  background: #000;
  color: #fff;
  border-radius: 24px;
  padding: 8px 16px;
  &:active {
    transform: scale(0.95);
  }
`

export const CheckoutButtonComponent = ({
  text,
  exitButtonText = 'Exit',
  styleProp,
  buttonColor = 'emrSecondary',
  additionalFunctionBeforeAction,
  additionalFunctionAfterAction,
  showExitButtonIfNotCheckedIn = false,
  startIcon,
  buttonSize = 'large',
  buttonVariant = 'contained',
}) => {
  const navigate = useNavigate()

  if (localStorage.getItem('checkedInDetails')) {
    return (
      <ExitButtonForCheckedInMentor
        additionalFunctionAfterAction={additionalFunctionAfterAction}
        additionalFunctionBeforeAction={additionalFunctionBeforeAction}
        styleProp={styleProp}
        text={text}
        buttonSize={buttonSize}
        buttonVariant={buttonVariant}
        buttonColor={buttonColor}
        startIcon={startIcon}
      />
    )
    // return (
    //   <>
    //     <CheckoutButton
    //       sx={{ textTransform: 'unset', ...styleProp }}
    //       size="large"
    //       variant="contained"
    //       color={buttonColor}
    //       onClick={async () => {
    //         additionalFunctionBeforeAction && additionalFunctionBeforeAction()
    //         const emrUsageLogData = localStorage.getItem('checkedInDetails')
    //         setLoading(true)
    //         await checkOutRoom(JSON.parse(emrUsageLogData))
    //         setLoading(false)
    //         localStorage.removeItem('checkedInDetails')
    //         additionalFunctionAfterAction && additionalFunctionAfterAction()
    //         navigate('/mentor-home/my-clinic-associations')
    //       }}
    //     >
    //       <ExitToAppIcon /> {`${text || 'Checkout'}`}
    //       <span style={{ marginRight: '12px' }}></span>
    //       {loading && <Spinner size="sm" color="light" />}
    //     </CheckoutButton>
    //     {loading && dialogOpen && (
    //       <Dialog
    //         open={true}
    //         PaperProps={{
    //           sx: {
    //             padding: '16px',
    //             margin: '8px',
    //             width: '400px',
    //             borderRadius: '8px',
    //             textAlign: 'center',
    //             borderTop: '3px solid #6B6CD1',
    //           },
    //         }}
    //       >
    //         <CloseIcon
    //           sx={{ cursor: 'pointer', position: 'absolute', top: '8px', right: '8px' }}
    //           onClick={() => setDialogOpen(false)}
    //         />
    //         <div style={{ margin: '24px 0' }}>
    //           Checking you out.. <Spinner size="sm" variant="dark" />{' '}
    //         </div>
    //       </Dialog>
    //     )}
    //   </>
    // )
  } else {
    if (showExitButtonIfNotCheckedIn) {
      return (
        <CheckoutButton
          variant={buttonVariant}
          startIcon={startIcon}
          color={buttonColor}
          style={{ ...styleProp }}
          onClick={async () => {
            localStorage.removeItem('peekedInRoomDetails')
            localStorage.removeItem('roomDataEventTypes')
            navigate('/mentor-home/my-clinic-associations')
          }}
          size={buttonSize}
        >
          {startIcon ? <></> : <ExitToAppIcon />} {exitButtonText}
          <span style={{ marginRight: '12px' }}></span>
        </CheckoutButton>
      )
    }
  }
}

function ExitButtonForCheckedInMentor({
  text,
  styleProp,
  buttonColor = 'emrSecondary',
  additionalFunctionBeforeAction,
  additionalFunctionAfterAction,
  buttonVariant,
  startIcon,
  buttonSize,
}) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(true)

  return (
    <>
      <CheckoutButton
        sx={{ textTransform: 'unset', ...styleProp }}
        size={buttonSize}
        variant={buttonVariant}
        startIcon={startIcon}
        color={buttonColor}
        onClick={async () => {
          additionalFunctionBeforeAction && additionalFunctionBeforeAction()
          const emrUsageLogData = localStorage.getItem('checkedInDetails')
          setLoading(true)
          await checkOutRoom(JSON.parse(emrUsageLogData))
          setLoading(false)
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('roomDataEventTypes')
          additionalFunctionAfterAction && additionalFunctionAfterAction()
          navigate('/mentor-home/my-clinic-associations')
        }}
      >
        {startIcon ? <></> : <ExitToAppIcon />} {`${text || 'Checkout'}`}
        <span style={{ marginRight: '12px' }}></span>
        {loading && <Spinner size="sm" color="light" />}
      </CheckoutButton>
      {loading && dialogOpen && (
        <Dialog
          open={true}
          PaperProps={{
            sx: {
              padding: '16px',
              margin: '8px',
              width: '400px',
              borderRadius: '8px',
              textAlign: 'center',
              borderTop: '3px solid #6B6CD1',
            },
          }}
        >
          <CloseIcon
            sx={{ cursor: 'pointer', position: 'absolute', top: '8px', right: '8px' }}
            onClick={() => setDialogOpen(false)}
          />
          <div style={{ margin: '24px 0' }}>
            Checking you out.. <Spinner size="sm" variant="dark" />{' '}
          </div>
        </Dialog>
      )}
    </>
  )
}

export function EmrCheckedInIdlePopup({
  currentClinicLocationData,
  clinicLocationId,
  mentorName = '',
  externalIsIdle = null, // Optional: external control of idle state
  onResetIdle = null, // Optional: callback when idle is reset
}) {
  const mentorClinicsContext = useContext(MentorClinicsContext)
  const associatedClinicsData = mentorClinicsContext?.associatedClinicsData || []

  let item = localStorage.getItem('checkedInDetails')
  let peekData = localStorage.getItem('peekedInRoomDetails')
  const parsedItem = useMemo(() => (item ? JSON.parse(item) : null), [item])
  const navigate = useNavigate()

  const peekedRoomData = useMemo(() => (peekData ? JSON.parse(peekData) : null), [peekData])

  // Use external idle state if provided, otherwise use internal hook
  const internalIdleHook = useIdleScreen({ idleTime: 180000, manualReset: true })
  const isIdle = externalIsIdle !== null ? externalIsIdle : internalIdleHook.isIdle
  const resetIdle = externalIsIdle !== null && onResetIdle ? onResetIdle : internalIdleHook.resetIdle
  // const { isIdle, resetIdle } = useIdleScreen({ idleTime: 3000, manualReset: true })

  const hasCheckedInBefore = !!parsedItem
  // mentorLastCheckedIn
  const hasCheckedInOneDayBefore =
    parsedItem && dayjs(parsedItem?.createdTime).isBefore(dayjs().subtract(1, 'day'))
  // const { isIdle, resetIdle } = useIdleScreen({ idleTime: 10000, manualReset: true });

  const checkedInOrPeekedInChamberRoomId = parsedItem?.chamberRoomId || peekedRoomData?.id

  const currentRoomName = parsedItem?.roomName || peekedRoomData?.title

  const isInPersonCheckInAvailable = peekedRoomData?.checkInMethodOptions
    ?.map((item) => item?.id)
    .includes('check-in-method-in-person')

  function refreshLocalStorageCheckInVars() {
    item = localStorage.getItem('checkedInDetails')
    peekData = localStorage.getItem('peekedInRoomDetails')
  }

  async function logAndResetIdle() {
    if (parsedItem) {
      await refreshCheckIntoRoomWithLocalStorageApplied(parsedItem)
    } else if (peekedRoomData) {
      presentInRoom(peekedRoomData)
    }
    resetIdle()
  }

  useEffect(() => {
    const handler = async (event) => {
      await sleep(500)
      refreshLocalStorageCheckInVars()
    }

    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handler)
    return () => {
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handler)
    }
  }, [])

  useEffect(() => {
    if (isIdle) {
      logMentorIdle(parsedItem || peekedRoomData)
    }
  }, [isIdle, parsedItem, peekedRoomData])

  useEffect(() => {
    resetIdle()
  }, [parsedItem])

  // useEffect(()=>{

  // })

  // if(!getAllRolesInBoolean()?.isMentor){
  //   return null
  // }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Shared content component
  const renderContent = () => (
    <>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6779e3 0%, #7450aa 100%)',
          color: 'white',
          padding: { xs: '18px 16px', sm: '22px 24px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <LockIcon sx={{ fontSize: { xs: 24, sm: 28 }, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '16px', sm: '17px' }, 
                mb: 0.5,
                letterSpacing: '-0.01em',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Screen paused
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.95, 
                fontSize: { xs: '12px', sm: '13px' },
                fontWeight: 400,
                letterSpacing: '0.01em',
              }}
            >
              {currentRoomName}
              {currentClinicLocationData?.title && ` • ${currentClinicLocationData.title}`}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={logAndResetIdle}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          padding: { xs: '24px 16px', sm: '28px 24px' },
          paddingBottom: { xs: '24px', sm: '28px' },
          textAlign: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: '100%',
          // Smooth scrolling
          WebkitOverflowScrolling: 'touch',
          // Modern scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
            margin: '8px 0',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(103, 121, 227, 0.3)',
            borderRadius: '4px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            '&:hover': {
              background: 'rgba(103, 121, 227, 0.5)',
              backgroundClip: 'padding-box',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(103, 121, 227, 0.3) rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 64, sm: 72 },
              height: { xs: 64, sm: 72 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.1) 0%, rgba(116, 80, 170, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: { xs: 32, sm: 36 }, color: '#6779e3' }} />
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '16px', sm: '17px' },
              color: '#0f172a',
              letterSpacing: '-0.01em',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Session paused
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: { xs: '13px', sm: '14px' },
              color: '#475569',
              lineHeight: 1.6,
              maxWidth: '400px',
              fontWeight: 400,
            }}
          >
            Your screen was automatically paused for privacy and security after a period of inactivity.
          </Typography>
        </Box>

        {parsedItem && (
          <Box
            sx={{
              padding: { xs: '12px', sm: '16px' },
              background: 'rgba(103, 121, 227, 0.05)',
              borderRadius: '10px',
              mb: { xs: 2, sm: 2.5 },
              border: '1px solid rgba(103, 121, 227, 0.1)',
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '12px', sm: '13px' },
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                fontWeight: 400,
              }}
            >
              <ScheduleIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              Last check-in:{' '}
              {
                getExactDiffFromCurrentTimeDayJsWithExtraData(
                  dayjs.utc(parsedItem?.createdTime).local(),
                  undefined,
                  false,
                ).differenceString
              }{' '}
              at {dayjs.utc(parsedItem?.createdTime).local().format(MOMENT_FORMATS.formattedTime24Hrs)}
            </Typography>
          </Box>
        )}

        {!(hasCheckedInBefore && !hasCheckedInOneDayBefore) && (
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: { xs: '13px', sm: '14px' },
              color: '#475569',
              mb: { xs: 1.5, sm: 2 },
              fontWeight: 400,
            }}
          >
            {parsedItem ? 'Re-check in' : 'Check in'} to resume your session
          </Typography>
        )}
      </DialogContent>

      <DialogActions 
        sx={{ 
          justifyContent: 'center', 
          padding: { xs: '16px', sm: '20px 24px' },
          paddingTop: { xs: '12px', sm: '16px' },
          flexShrink: 0, 
          gap: { xs: 1.25, sm: 1.5 },
          flexWrap: 'wrap',
        }}
      >
        <CheckoutButtonComponent
          additionalFunctionAfterAction={() => resetIdle()}
          buttonVariant="outlined"
          text="Checkout"
          exitButtonText="Checkout"
          showExitButtonIfNotCheckedIn
          styleProp={{ 
            textTransform: 'none', 
            fontWeight: 500,
            fontSize: { xs: '13px', sm: '14px' },
          }}
          buttonSize="medium"
          startIcon={<ExitToAppIcon />}
        />
        {hasCheckedInBefore && !hasCheckedInOneDayBefore && (
          <Button
            variant="contained"
            color="emrSecondary"
            onClick={() => {
              refreshCheckIntoRoomWithLocalStorageApplied(parsedItem)
              resetIdle()
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '13px', sm: '14px' },
              px: { xs: 2.5, sm: 3 },
              py: { xs: 1, sm: 1.25 },
            }}
          >
            Resume session
          </Button>
        )}
        {hasCheckedInOneDayBefore && (
          <Button
            variant="contained"
            color="emrSecondary"
            onClick={() => {
              refreshCheckIntoRoomWithLocalStorageApplied(parsedItem)
              resetIdle()
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '13px', sm: '14px' },
              px: { xs: 2.5, sm: 3 },
              py: { xs: 1, sm: 1.25 },
            }}
          >
            Resume session
          </Button>
        )}
        {!hasCheckedInBefore && isInPersonCheckInAvailable && (
          <>
            <Button
              disabled={!isInPersonCheckInAvailable}
              onClick={() => {
                checkIntoRoomWithLocalStorageApplied({
                  ...peekedRoomData,
                  checkinMethod: 'check-in-method-in-person',
                })
                resetIdle()
              }}
              variant="contained"
              color="emrSecondary"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '13px', sm: '14px' },
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1, sm: 1.25 },
              }}
            >
              Check in
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                logAndResetIdle(peekedRoomData)
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '13px', sm: '14px' },
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1, sm: 1.25 },
                borderColor: '#6779e3',
                color: '#6779e3',
                '&:hover': {
                  borderColor: '#5568d3',
                  backgroundColor: 'rgba(103, 121, 227, 0.05)',
                },
              }}
            >
              Continue invisibly
            </Button>
          </>
        )}
      </DialogActions>
    </>
  )

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isIdle) {
      // Lock body scroll when popup opens
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      
      return () => {
        // Restore body scroll when popup closes
        document.body.style.overflow = originalStyle
      }
    }
  }, [isIdle])

  // Mobile: Drawer from bottom with full modal behavior
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={isIdle}
        onClose={resetIdle}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            maxHeight: '95vh',
            height: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.12), 0 -4px 16px rgba(0, 0, 0, 0.08)',
          },
        }}
        ModalProps={{
          // Block background interaction
          disableScrollLock: false,
        }}
        sx={{
          zIndex: 1300,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          },
        }}
      >
        {/* Drag handle indicator */}
        <Box
          sx={{
            width: '48px',
            height: '5px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px',
            margin: '12px auto 8px',
            cursor: 'grab',
            flexShrink: 0,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
        {renderContent()}
      </Drawer>
    )
  }

  // Desktop: Dialog with full modal behavior
  return (
    <IdlePopupaStyledDialog 
      open={isIdle}
      onClose={resetIdle}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      }}
      sx={{
        zIndex: 1300,
      }}
    >
      {renderContent()}
    </IdlePopupaStyledDialog>
  )
}

export const CheckoutButton = styled(Button)`
  border-radius: 24px;
  padding: 8px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const CheckInInformationComponent = ({ minimal = false, currentClinic }) => {
  const [checkInModalOpen, setCheckInModalOpen] = useState(false)
  const [checkedInDetails, setCheckedInDetails] = useState(() =>
    localStorage.getItem('checkedInDetails'),
  )
  const [roomDataStringified, setRoomDataStringified] = useState(() =>
    localStorage.getItem('peekedInRoomDetails'),
  )

  const navigate = useNavigate()
  const roomData = roomDataStringified ? JSON.parse(roomDataStringified) : null

  useEffect(() => {
    const interval = setInterval(() => {
      const newCheckIn = localStorage.getItem('checkedInDetails')
      const newPeek = localStorage.getItem('peekedInRoomDetails')

      setCheckedInDetails((prev) => (prev !== newCheckIn ? newCheckIn : prev))
      setRoomDataStringified((prev) => (prev !== newPeek ? newPeek : prev))
    }, 1000) // runs every second

    return () => clearInterval(interval)
  }, [])

  if (!checkedInDetails) {
    return (
      <>
        <CheckoutButton
          variant="contained"
          color="emrSecondary"
          size="small"
          sx={{ flexDirection: 'column', fontSize: '12px', padding: '0px', height: '100%' }}
          onClick={() => setCheckInModalOpen(true)}
        >
          <LoginIcon />
          Check In
        </CheckoutButton>
        {checkInModalOpen && (
          <CheckInDialogComponent
            closeFunction={() => setCheckInModalOpen(false)}
            roomData={roomData}
            clinicLocationData={currentClinic}
            onChoosingInPerson={async () => {
              await sleep(500)
              setCheckedInDetails(localStorage.getItem('checkedInDetails'))
              navigate(
                `/mentor-home/my-clinic-associations/${currentClinic?.clinicBrandId}/${currentClinic?.id}`,
              )
            }}
            onChoosingRemote={() =>
              navigate(
                `/mentor-home/join-chamber-room/${currentClinic?.clinicBrandId}/${currentClinic?.id}`,
              )
            }
          />
        )}
      </>
    )
  }

  return <CheckedInfo minimal={minimal} />
}

function CheckedInfo({ minimal }) {
  let item = localStorage.getItem('checkedInDetails')
  const parsedItem = JSON.parse(item)
  // const { isIdle, resetIdle } = useIdleScreen({ idleTime: 3000, manualReset: true })
  const { mentorProfileData } = useContext(MentorProfileContext)
  return (
    <div
      style={{
        fontSize: '12px',
        background: MUIThemeConfig.palette.emrSecondary.main,
        color: '#fff',
        borderRadius: '0',
        padding: '4px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: minimal ? 'unset' : '160px',
      }}
    >
      {!minimal && <div>{mentorProfileData?.name || mentorProfileData?.firstName}</div>}
      <span>
        <span>Checked In: </span>
        <b>{dayjs.utc(parsedItem?.createdTime).local().format('h:mm a')}</b>
      </span>
      {/* <IdlePopupaStyledDialog open={isIdle}>
        <DialogTitle>Are you there?</DialogTitle>
        <DialogContent>
          <div>You seem to have gone idle for a while.</div>
          <div>
            You had checked in at {dayjs.utc(parsedItem?.createdTime).local().format('h:mm a')}
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              resetIdle()
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </IdlePopupaStyledDialog> */}
      {!minimal && <div>{parsedItem?.roomName}</div>}
    </div>
  )
}
